/**
 * workday.ts — 업무일/휴일 도메인 타입 + 영업일 계산 헬퍼 (조직 공통).
 *
 * 저장소: system_settings/workday_config (services/workday_config.repository.ts)
 *   - weeklyOff : 정기 휴무 요일 (0=일 … 6=토). 기본 [0] (일요일)
 *   - holidays  : 공휴일/임시공휴일/회사휴일 목록 (양력 날짜)
 *
 * 여기 헬퍼(isWorkday/nextWorkday/workdayCount …)는 순수 함수로, 화면(달력 하이라이트·
 * 견적 납기·영업일 통계)과 추후 백엔드 동기화 양쪽에서 동일 규칙으로 끌어쓸 수 있게 한다.
 */

/** 휴일 종류 — public=법정공휴일, temp=임시공휴일, custom=회사휴일 */
export type HolidayKind = "public" | "temp" | "custom";

export interface Holiday {
  /** "YYYY-MM-DD" (양력) */
  date: string;
  name: string;
  kind: HolidayKind;
  /** manual=수동 입력, api=천문연 공공API 자동 불러오기 */
  source?: "manual" | "api";
}

export interface WorkdayConfig {
  /** 정기 휴무 요일 (0=일 … 6=토) */
  weeklyOff: number[];
  holidays: Holiday[];
}

export const DEFAULT_WORKDAY_CONFIG: WorkdayConfig = {
  weeklyOff: [0], // 일요일
  holidays: [],
};

export const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"] as const;

export const HOLIDAY_KINDS: HolidayKind[] = ["public", "temp", "custom"];

export const HOLIDAY_KIND_LABEL: Record<HolidayKind, string> = {
  public: "법정공휴일",
  temp: "임시공휴일",
  custom: "회사휴일",
};

/** "YYYY-MM-DD" → 로컬 Date (유효하지 않으면 null) */
function toDate(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  // 윤년/말일 오버플로 검증 (예: 2026-02-30 → 무효)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** Date → "YYYY-MM-DD" (로컬 기준) */
export function fmtYmd(dt: Date): string {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** 요일 (0=일 … 6=토), 무효 날짜면 -1 */
export function dayOfWeek(ymd: string): number {
  const dt = toDate(ymd);
  return dt ? dt.getDay() : -1;
}

export function isValidYmd(ymd: string): boolean {
  return toDate(ymd) !== null;
}

/** 정기 휴무 요일인지 */
export function isWeeklyOff(ymd: string, config: WorkdayConfig): boolean {
  const dow = dayOfWeek(ymd);
  return dow >= 0 && config.weeklyOff.includes(dow);
}

/** 해당 날짜의 휴일 항목 (없으면 null) */
export function findHoliday(ymd: string, config: WorkdayConfig): Holiday | null {
  return config.holidays.find((h) => h.date === ymd) ?? null;
}

export function isHoliday(ymd: string, config: WorkdayConfig): boolean {
  return findHoliday(ymd, config) !== null;
}

/** 영업일 = 정기 휴무 요일도 아니고 지정 휴일도 아닌 날 */
export function isWorkday(ymd: string, config: WorkdayConfig): boolean {
  return isValidYmd(ymd) && !isWeeklyOff(ymd, config) && !isHoliday(ymd, config);
}

/** 휴무 사유 라벨 (영업일이면 null) — UI 툴팁용 */
export function nonWorkdayReason(ymd: string, config: WorkdayConfig): string | null {
  const hol = findHoliday(ymd, config);
  if (hol) return hol.name || HOLIDAY_KIND_LABEL[hol.kind];
  if (isWeeklyOff(ymd, config)) {
    const dow = dayOfWeek(ymd);
    return dow >= 0 ? `${WEEKDAY_LABELS[dow]}요일 휴무` : "휴무";
  }
  return null;
}

/** ymd 다음의 첫 영업일 (자기 자신 제외) */
export function nextWorkday(ymd: string, config: WorkdayConfig): string {
  const dt = toDate(ymd);
  if (!dt) return ymd;
  for (let i = 0; i < 400; i++) {
    dt.setDate(dt.getDate() + 1);
    const s = fmtYmd(dt);
    if (isWorkday(s, config)) return s;
  }
  return ymd;
}

/** ymd 이전의 첫 영업일 (자기 자신 제외) */
export function prevWorkday(ymd: string, config: WorkdayConfig): string {
  const dt = toDate(ymd);
  if (!dt) return ymd;
  for (let i = 0; i < 400; i++) {
    dt.setDate(dt.getDate() - 1);
    const s = fmtYmd(dt);
    if (isWorkday(s, config)) return s;
  }
  return ymd;
}

/** from~to(양끝 포함) 사이 영업일 수 */
export function workdayCount(from: string, to: string, config: WorkdayConfig): number {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b || a > b) return 0;
  let n = 0;
  const cur = new Date(a);
  while (cur <= b) {
    if (isWorkday(fmtYmd(cur), config)) n++;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
}

/** 특정 연도의 휴일 목록 (날짜 오름차순) */
export function holidaysInYear(config: WorkdayConfig, year: number): Holiday[] {
  const pfx = `${year}-`;
  return config.holidays
    .filter((h) => h.date.startsWith(pfx))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** 휴일이 등록된 연도 목록 (오름차순) */
export function holidayYears(config: WorkdayConfig): number[] {
  const set = new Set<number>();
  for (const h of config.holidays) {
    const y = Number(h.date.slice(0, 4));
    if (Number.isFinite(y)) set.add(y);
  }
  return [...set].sort((a, b) => a - b);
}
